import { defineNuxtPlugin, useRuntimeConfig } from 'nuxt/app'

import { createInstance, getInstance, type ModuleFederation } from '@module-federation/runtime'

import { createMfCacheBustStamp, createRemoteEntryCacheBustPlugin, withMfCacheBust } from '../utils/module-federation-cache-bust'

// Module Federation runtime bootstrap for Client Portal host.
// Same pattern as docs/MODULE_FEDERATION_GUIDE.md — do not use @module-federation/vite.
//
// Why we expose `instance.loadRemote` (and NOT the top-level `loadRemote`):
//   Top-level `loadRemote` can throw RUNTIME-009 under HMR / timing. Binding
//   `$loadRemote` to the cached instance method avoids that.

let mfInstance: ModuleFederation | null = null

// One stamp per app boot: the manifest and remote entry are re-fetched at most
// once per session even if the GCS bucket serves them with immutable caching.
const mfSessionStamp = createMfCacheBustStamp()

const ensureMfInstance = (manifestUrl: string): ModuleFederation => {
  const existing = mfInstance ?? getInstance()
  if (existing) {
    mfInstance = existing
    return existing
  }

  mfInstance = createInstance({
    name: 'clientPortalApp',
    remotes: [
      {
        name: 'membershipPreviewApp',
        entry: manifestUrl,
        shareScope: 'default',
      },
    ],
    shared: {
      'vue-i18n': { singleton: true, requiredVersion: false, eager: false },
    },
    plugins: [createRemoteEntryCacheBustPlugin(mfSessionStamp)],
  })

  if (import.meta.dev) {
    console.info('[MF] Federation runtime created for host "clientPortalApp" with manifest:', manifestUrl)
  }

  return mfInstance
}

const loadConfiguredRemote = async <T = unknown>(manifestUrl: string, id: string): Promise<T> => {
  const remoteModule = await ensureMfInstance(manifestUrl).loadRemote<T>(id)
  if (!remoteModule) {
    throw new Error(`[MF] Remote module "${id}" resolved to null.`)
  }

  return remoteModule
}

const probeManifestInDev = async (manifestUrl: string) => {
  if (!import.meta.dev) return

  try {
    const response = await fetch(manifestUrl, { method: 'GET', mode: 'cors' })
    if (!response.ok) {
      console.warn(
        `[MF] Manifest probe failed (${response.status}): ${manifestUrl}. ` +
          'Start membership-hmi-preview on :4040 or set MEMBERSHIP_PREVIEW_REMOTE_URL ' +
          'to mf-manifest.json'
      )
    }
  } catch {
    console.warn(
      `[MF] Manifest probe failed: ${manifestUrl}. ` +
        'Start membership-hmi-preview on :4040 or set MEMBERSHIP_PREVIEW_REMOTE_URL ' +
        'to mf-manifest.json'
    )
  }
}

export default defineNuxtPlugin(() => {
  const { membershipPreviewMfManifestUrl } = useRuntimeConfig().public as {
    membershipPreviewMfManifestUrl?: string
  }

  // Bust browser + shared-edge caches once per session so a stale immutable
  // manifest never resolves chunk hashes from a previous remote build.
  const manifestUrl = membershipPreviewMfManifestUrl ? withMfCacheBust(membershipPreviewMfManifestUrl, mfSessionStamp) : undefined

  if (typeof window !== 'undefined' && manifestUrl) {
    ensureMfInstance(manifestUrl)
    void probeManifestInDev(manifestUrl)
  }

  return {
    provide: {
      loadRemote: <T = unknown>(id: string): Promise<T> => {
        if (!manifestUrl) {
          return Promise.reject(
            new Error(
              '[MF] membershipPreviewMfManifestUrl is not configured. ' +
                'Set MEMBERSHIP_PREVIEW_REMOTE_URL or fix runtimeConfig.public.membershipPreviewMfManifestUrl in nuxt.config.ts.'
            )
          )
        }
        return loadConfiguredRemote<T>(manifestUrl, id)
      },
    },
  }
})
